import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";

/**
 * A month grid, on react-day-picker, drawn in the project's own tokens (#555).
 *
 * Written by hand rather than with `shadcn add calendar`, which is how a
 * customised primitive gets reset (see CLAUDE.md). The selected day is the
 * gold primary; a range's middle is the muted fill, so the two ends read as
 * the choice and the days between as its extent.
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      data-slot="calendar"
      showOutsideDays={showOutsideDays}
      className={cn("p-1", className)}
      classNames={{
        root: "relative w-fit",
        months: "flex flex-col gap-4 sm:flex-row",
        month: "flex flex-col gap-3",
        month_caption: "flex h-8 items-center justify-center px-8",
        caption_label: "text-sm font-medium",
        nav: "absolute inset-x-1 top-1 flex h-8 items-center justify-between",
        button_previous:
          "text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring/50 inline-flex size-7 items-center justify-center rounded-md outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-40",
        button_next:
          "text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring/50 inline-flex size-7 items-center justify-center rounded-md outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-40",
        month_grid: "border-collapse",
        weekdays: "flex",
        weekday:
          "text-muted-foreground flex size-8 items-center justify-center text-xs font-normal",
        week: "mt-1 flex",
        day: "size-8 p-0 text-center text-sm",
        day_button:
          "hover:bg-accent focus-visible:ring-ring/50 inline-flex size-8 items-center justify-center rounded-md tabular-nums outline-none focus-visible:ring-[3px] disabled:pointer-events-none",
        today: "[&>button]:font-semibold [&>button]:underline",
        outside: "text-muted-foreground/60",
        disabled: "text-muted-foreground/40",
        hidden: "invisible",
        selected:
          "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary/90",
        range_start: "bg-muted rounded-l-md",
        range_end: "bg-muted rounded-r-md",
        range_middle:
          "bg-muted [&>button]:text-foreground [&>button]:hover:bg-accent [&>button]:bg-transparent",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: chevronClassName }) =>
          orientation === "left" ? (
            <ChevronLeft className={cn("size-4", chevronClassName)} />
          ) : (
            <ChevronRight className={cn("size-4", chevronClassName)} />
          ),
      }}
      {...props}
    />
  );
}

export { Calendar };
